import CheckmarkOutline16 from '@carbon/icons-react/es/checkmark--outline/16';
import DotMark16 from '@carbon/icons-react/es/dot-mark/16';
import { GridApi } from 'ag-grid-community';
import { Space } from 'antd';
import moment from 'moment';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { User } from 'api/models';
import { Button } from 'components/atoms/button';
import { ProgressPie } from 'components/atoms/progress-pie';
import { RelativeTime } from 'components/atoms/relative-time';
import { getLatestReminderAt } from '../approval-all-user-view/all-user-view';
import { FormatTime } from '../common/format-time';
import { TimesheetReminder, TimesheetStatus, UserLevelSummary } from '../types';

const ReminderCellWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
`;

const DotMark = styled(DotMark16)``;

const LowerCase = styled.span`
  text-transform: lowercase;
`;

const Wrapper = styled(Space)`
  color: var(--gray-40);

  ${DotMark} {
    width: var(--spacing-03);
    height: var(--spacing-03);
  }
`;

const StyledWrapper = styled(Wrapper)`
  display: flex;
  align-items: center;
  width: 176px;

  .ant-space-item:nth-child(2) {
    font-weight: bold;
  }
`;

const ProgressPieStyled = styled(ProgressPie)`
  margin-right: var(--spacing-02);
  position: relative;
  top: var(--spacing-01);
`;

const FormatTimeStyled = styled(FormatTime)`
  margin-right: var(--spacing-03);
  height: 100%;
`;

const SentText = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
`;

const CheckMark = styled(CheckmarkOutline16)`
  color: var(--green-40);
`;

const ReminderButton = styled(Button)`
  color: var(--gray-40);
  text-decoration: underline;
  padding-left: var(--spacing-02);
  padding-right: var(--spacing-02);
`;

const ReminderSent = ({ lastReminderAt }) => (
  <SentText>
    <CheckMark />
    <FormattedMessage
      id="timesheets.text.reminder_sent"
      values={{
        time: (
          <LowerCase>
            <RelativeTime time={moment(lastReminderAt)} />{' '}
          </LowerCase>
        ),
      }}
    />
  </SentText>
);

interface Props {
  onUserSelection: (userId: number, projectId?: number) => void;
  sendReminder: (user: User, projectId: number) => Promise<TimesheetReminder>;
  lastReminderAt: number;
  totalMinutes: number;
  timesheet: UserLevelSummary;
  api: GridApi;
  isManagerApproval: boolean;
}
export const ReminderCell: FC<Props> = ({
  onUserSelection,
  lastReminderAt,
  sendReminder,
  totalMinutes,
  timesheet,
  api,
  isManagerApproval,
}) => {
  const showReminder =
    lastReminderAt !== 0 &&
    moment().diff(moment(lastReminderAt), 'minutes') < 30;

  const onSendReminder = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      sendReminder(timesheet.user, timesheet?.projectId).then((reminder) => {
        const row = api?.getRowNode(
          `${timesheet.userId}_${timesheet.projectId}`,
        );

        row?.updateData({
          ...timesheet,
          lastReminderAt:
            timesheet.status === TimesheetStatus.NotSubmitted
              ? getLatestReminderAt(timesheet.reminders.concat(reminder))
              : 0,
          reminders:
            timesheet.status === TimesheetStatus.NotSubmitted
              ? timesheet.reminders.concat(reminder)
              : undefined,
        });
      });
    },
    [api, sendReminder, timesheet],
  );

  return (
    <ReminderCellWrapper
      onClick={() =>
        isManagerApproval
          ? onUserSelection(timesheet.userId)
          : onUserSelection(timesheet.userId, timesheet.projectId)
      }
    >
      <StyledWrapper size={4}>
        <ProgressPieStyled progress={25} type="success" />
        <FormatTimeStyled value={totalMinutes} showHypenIfZero={false} />
        <FormattedMessage id="tracked" />
      </StyledWrapper>
      <Wrapper size={4}>
        <FormattedMessage id="timesheets.text.yet_to_submit" />
        <DotMark />
        <ReminderButton
          size="small"
          type="text"
          onClick={(e) => onSendReminder(e)}
        >
          <FormattedMessage id="timesheets.buttons.send_reminder" />
        </ReminderButton>
        {showReminder ? (
          <>
            <DotMark />
            <ReminderSent lastReminderAt={lastReminderAt} />
          </>
        ) : null}
      </Wrapper>
    </ReminderCellWrapper>
  );
};
